import React, { useEffect, useState } from 'react';
import { formatPrice } from '../services/formatPrice';
import useCartCost, { checkIsTaxEnabled } from '../hooks/useCartCost';
import useCheckoutState from '../hooks/useCheckoutState';

import { upperFirst } from 'lodash';
import checkHasValidStartDate from '../lib/checkHasValidStartDate';
import { format } from 'date-fns';
import checkHasTrialPeriod from '../lib/checkHasTrialPeriod';
import { getVatLocale } from '../lib/locale/vat-locale';
import checkShouldShowPriceBreakdown from '../lib/checkShouldShowPriceBreakdown';
import clsx from 'clsx';
import { InfoTooltip } from './Tooltip';
import { useCheckoutLocalization } from '@repo/checkout/hooks/useCheckoutLocalization';

function PriceBreakdownContainer({ children }) {
  return (
    <div className="cp_price-breakdown-wrapper text-gray-600 text-sm flex flex-grow flex-col justify-end items-end space-y-0.5">
      {children}
    </div>
  );
}

function PriceBreakdownRow({ children, type }) {
  return (
    <div
      className={`cp_price-breakdown-item cp_price-breakdown-item--${type} flex gap-x-1 items-end text-right`}
    >
      {children}
    </div>
  );
}

function PriceBreakdownLabel({ label, type }) {
  if (typeof label === 'string') {
    return (
      <span className={`cp_price-breakdown-label cp_price-breakdown-label--${type}`}>
        {upperFirst(label)}:{' '}
      </span>
    );
  }

  return (
    <span className={`cp_price-breakdown-label cp_price-breakdown-label--${type}`}>{label}</span>
  );
}

function PriceBreakdownValue({ children, type }) {
  const { isLoading } = useCartCost();

  return (
    <span
      className={clsx('cp_price-breakdown-value', `cp_price-breakdown-value--${type}`, {
        'blur-[4px]': isLoading,
      })}
    >
      {children}
    </span>
  );
}

export default function PriceBreakdown() {
  const cartCost = useCartCost();
  const { page, product, seller, form } = useCheckoutState();

  const shouldShowPriceBreakdown = checkShouldShowPriceBreakdown({
    pageType: page.type,
    productType: product.type,
    cartCost: cartCost,
    productVariants: product.variants,
    formVariants: form.variants,
    pageTicketGroups: page.ticketGroups,
  });

  if (!shouldShowPriceBreakdown) return null;

  const taxEnabled = checkIsTaxEnabled({ seller, page });

  const isSubscription = product.type === 'subscription';

  if (isSubscription) {
    if (taxEnabled) {
      return <SubscriptionTaxEnabledPriceBreakdown />;
    } else {
      return <SubscriptionRegularPriceBreakdown />;
    }
  } else {
    if (taxEnabled) {
      return <TaxEnabledPriceBreakdown />;
    } else {
      return <RegularPriceBreakdown />;
    }
  }
}

function SubscriptionTaxEnabledPriceBreakdown() {
  const cartCost = useCartCost();
  const { checkout, product } = useCheckoutState();
  const { locale } = useCheckoutLocalization();

  const hasTrialPeriod = checkHasTrialPeriod(product.trialPeriodDays);
  const hasStartDate = checkHasValidStartDate(product.startDate);
  const hasSetupFee = cartCost.setupFeeAmount > 0;
  const hasCoupon = !!cartCost.coupon;

  return (
    <PriceBreakdownContainer>
      <PriceBreakdownRow type="subtotal">
        <PriceBreakdownLabel label={checkout.cartSubtotalText} type="subtotal" />
        <PriceBreakdownValue type="subtotal">
          {formatPrice({
            amount: cartCost.subtotalAmount,
            currency: cartCost.currency,
            locale: checkout.locale,
            style: 'currency',
          })}
        </PriceBreakdownValue>
      </PriceBreakdownRow>

      {hasSetupFee && (
        <PriceBreakdownRow type="setup-fee">
          <PriceBreakdownLabel label={checkout.cartSetupFeeText} type="setup-fee" />
          <PriceBreakdownValue type="setup-fee">
            {formatPrice({
              amount: cartCost.setupFeeAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasCoupon && (
        <PriceBreakdownRow type="discount">
          <PriceBreakdownLabel
            label={
              cartCost.coupon.percentOff
                ? `${checkout.cartCouponDiscountText} (${cartCost.coupon.percentOff}%)`
                : checkout.cartCouponDiscountText
            }
            type="discount"
          />
          <PriceBreakdownValue type="discount">
            -
            {formatPrice({
              amount: cartCost.totalDiscountAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.fees &&
        cartCost.fees.map(fee => (
          <PriceBreakdownRow key={fee.name} type="fee">
            <PriceBreakdownLabel
              label={fee.percentage ? `${fee.name} (${fee.percentage}%)` : fee.name}
              type="fee"
            />
            <PriceBreakdownValue type="fee">
              {formatPrice({
                amount: fee.amount,
                currency: cartCost.currency,
                locale: checkout.locale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        ))}

      {!cartCost.hasProration && (
        <>
          {cartCost.totalTaxAmounts?.length ? (
            cartCost.totalTaxAmounts?.map(taxAmount => {
              const taxDisplayName =
                taxAmount.displayName &&
                (taxAmount.taxType === 'vat' || taxAmount.taxType === 'VAT')
                  ? getVatLocale(locale || checkout.locale || 'en-US', taxAmount.displayName)
                  : taxAmount.displayName;

              return (
                <PriceBreakdownRow key={taxAmount.displayName} type="tax">
                  <PriceBreakdownLabel
                    label={
                      <>
                        {taxDisplayName || checkout.cartTaxText} ({taxAmount.percentage}%)
                        <InfoTooltip>Tax is determined by billing information</InfoTooltip>:{' '}
                      </>
                    }
                    type="tax"
                  />

                  <PriceBreakdownValue type="tax">
                    {formatPrice({
                      amount: taxAmount.amount,
                      currency: cartCost.currency,
                      locale: checkout.locale,
                      style: 'currency',
                    })}
                  </PriceBreakdownValue>
                </PriceBreakdownRow>
              );
            })
          ) : (
            <PriceBreakdownRow type="tax">
              <PriceBreakdownLabel label={checkout.cartTaxText} type="tax" />
              <PriceBreakdownValue type="tax">
                {formatPrice({
                  amount: cartCost.recurringTaxAmount,
                  currency: cartCost.currency,
                  locale: checkout.locale,
                  style: 'currency',
                })}
              </PriceBreakdownValue>
            </PriceBreakdownRow>
          )}
        </>
      )}

      {cartCost.hasProration && (
        <>
          {cartCost.totalTaxAmountsBeforeProration?.length ? (
            cartCost.totalTaxAmountsBeforeProration?.map(taxAmount => {
              const taxDisplayName =
                taxAmount.displayName &&
                (taxAmount.taxType === 'vat' || taxAmount.taxType === 'VAT')
                  ? getVatLocale(locale || checkout.locale || 'en-US', taxAmount.displayName)
                  : taxAmount.displayName;

              return (
                <PriceBreakdownRow key={taxAmount.displayName} type="tax">
                  <PriceBreakdownLabel
                    label={
                      <>
                        {taxDisplayName || checkout.cartTaxText} ({taxAmount.percentage}%)
                        <InfoTooltip>Tax is determined by billing information</InfoTooltip>:{' '}
                      </>
                    }
                    type="tax"
                  />

                  <PriceBreakdownValue type="tax">
                    {formatPrice({
                      amount: taxAmount.amount,
                      currency: cartCost.currency,
                      locale: checkout.locale,
                      style: 'currency',
                    })}
                  </PriceBreakdownValue>
                </PriceBreakdownRow>
              );
            })
          ) : (
            <PriceBreakdownRow type="tax">
              <PriceBreakdownLabel label={checkout.cartTaxText} type="tax" />
              <PriceBreakdownValue type="tax">
                {formatPrice({
                  amount: cartCost.recurringTaxAmountBeforeProration,
                  currency: cartCost.currency,
                  locale: checkout.locale,
                  style: 'currency',
                })}
              </PriceBreakdownValue>
            </PriceBreakdownRow>
          )}
        </>
      )}

      {hasStartDate && (
        <PriceBreakdownRow type="start-date">
          <PriceBreakdownLabel
            label={`${checkout.cartTotalOnStartDateText} ${format(
              new Date(product.startDate),
              'd MMM, yyyy',
            )}`}
            type="start-date"
          />

          <PriceBreakdownValue type="start-date">
            {formatPrice({
              amount: cartCost.recurringTotalAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasTrialPeriod && (
        <PriceBreakdownRow type="trial">
          <PriceBreakdownLabel label={checkout.cartTotalAfterTrialText} type="trial" />
          <PriceBreakdownValue type="trial">
            {formatPrice({
              amount: cartCost.recurringTotalAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {!cartCost.hasProration && (
        <PriceBreakdownRow type="total">
          <PriceBreakdownLabel label={checkout.cartTotalDueText} type="total" />
          <PriceBreakdownValue type="total">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.hasProration && (
        <>
          <PriceBreakdownRow type="total">
            <PriceBreakdownLabel label={checkout.cartTotalDueText} type="total" />
            <PriceBreakdownValue type="total">
              {formatPrice({
                amount: cartCost.totalDueAmountBeforeProration,
                currency: cartCost.currency,
                locale: checkout.locale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>

          <PriceBreakdownRow type="proration">
            <PriceBreakdownLabel label={checkout.cartTotalDueProratedAmountText} type="proration" />
            <PriceBreakdownValue type="proration">
              {formatPrice({
                amount: cartCost.totalDueAmount,
                currency: cartCost.currency,
                locale: checkout.locale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        </>
      )}
    </PriceBreakdownContainer>
  );
}

function SubscriptionRegularPriceBreakdown() {
  const { checkout, product } = useCheckoutState();
  const cartCost = useCartCost();

  const isPaymentPlan = cartCost.planIterations >= 1;
  const hasTrialPeriod = checkHasTrialPeriod(product.trialPeriodDays);
  const hasStartDate = checkHasValidStartDate(product.startDate);
  const hasSetupFee = cartCost.setupFeeAmount > 0;
  const hasCoupon = !!cartCost.coupon;
  const hasDiscount = cartCost.totalDiscountAmount > 0;
  const hasFees = !!cartCost.fees;
  const shouldShowSubtotal =
    isPaymentPlan || hasTrialPeriod || hasStartDate || hasSetupFee || hasDiscount || hasFees;

  return (
    <PriceBreakdownContainer>
      {shouldShowSubtotal && (
        <PriceBreakdownRow type="subtotal">
          <PriceBreakdownLabel label={checkout.cartSubtotalText} type="subtotal" />
          <PriceBreakdownValue type="subtotal">
            {formatPrice({
              amount: cartCost.subtotalAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasSetupFee && (
        <PriceBreakdownRow type="setup-fee">
          <PriceBreakdownLabel label={checkout.cartSetupFeeText} type="setup-fee" />
          <PriceBreakdownValue type="setup-fee">
            {formatPrice({
              amount: cartCost.setupFeeAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasCoupon && (
        <PriceBreakdownRow type="discount">
          <PriceBreakdownLabel
            label={
              cartCost.coupon.percentOff
                ? `${checkout.cartCouponDiscountText} (${cartCost.coupon.percentOff}%)`
                : checkout.cartCouponDiscountText
            }
            type="discount"
          />
          <PriceBreakdownValue type="discount">
            -
            {formatPrice({
              amount: cartCost.totalDiscountAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasTrialPeriod && (
        <PriceBreakdownRow type="trial">
          <PriceBreakdownLabel label={checkout.cartTotalAfterTrialText} type="trial" />
          <PriceBreakdownValue type="trial">
            {formatPrice({
              amount: cartCost.recurringTotalAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasStartDate && (
        <PriceBreakdownRow type="start-date">
          <PriceBreakdownLabel
            label={`${checkout.cartTotalOnStartDateText} ${format(
              new Date(product.startDate),
              'd MMM, yyyy',
            )}`}
            type="start-date"
          />

          <PriceBreakdownValue type="start-date">
            {formatPrice({
              amount: cartCost.recurringTotalAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.fees &&
        cartCost.fees.map(fee => (
          <PriceBreakdownRow key={fee.name} type="fee">
            <PriceBreakdownLabel
              label={fee.percentage ? `${fee.name} (${fee.percentage}%)` : fee.name}
              type="fee"
            />
            <PriceBreakdownValue type="fee">
              {formatPrice({
                amount: fee.amount,
                currency: cartCost.currency,
                locale: checkout.locale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        ))}

      {!cartCost.hasProration && (
        <PriceBreakdownRow type="total">
          <PriceBreakdownLabel label={checkout.cartTotalDueText} type="total" />
          <PriceBreakdownValue type="total">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.hasProration && (
        <>
          <PriceBreakdownRow type="total">
            <PriceBreakdownLabel label={checkout.cartTotalDueText} type="total" />
            <PriceBreakdownValue type="total">
              {formatPrice({
                amount: cartCost.totalDueAmountBeforeProration,
                currency: cartCost.currency,
                locale: checkout.locale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>

          <PriceBreakdownRow type="proration">
            <PriceBreakdownLabel label={checkout.cartTotalDueProratedAmountText} type="proration" />
            <PriceBreakdownValue type="proration">
              {formatPrice({
                amount: cartCost.totalDueAmount,
                currency: cartCost.currency,
                locale: checkout.locale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        </>
      )}
    </PriceBreakdownContainer>
  );
}

function TaxEnabledPriceBreakdown() {
  const cartCost = useCartCost();
  const { checkout, paymentOption } = useCheckoutState();

  const hasCoupon = !!cartCost.coupon;
  const hasDiscount = cartCost.totalDiscountAmount > 0;
  const hasBulkDiscount = cartCost.totalBulkDiscountAmount > 0;

  return (
    <PriceBreakdownContainer>
      <PriceBreakdownRow type="subtotal">
        <PriceBreakdownLabel label={checkout.cartSubtotalText} type="subtotal" />
        <PriceBreakdownValue type="subtotal">
          {formatPrice({
            amount: cartCost.subtotalAmount,
            currency: cartCost.currency,
            locale: checkout.locale,
            style: 'currency',
          })}
        </PriceBreakdownValue>
      </PriceBreakdownRow>

      {cartCost.setupFeeAmount > 0 && (
        <PriceBreakdownRow type="setup-fee">
          <PriceBreakdownLabel label={checkout.cartSetupFeeText} type="setup-fee" />
          <PriceBreakdownValue type="setup-fee">
            {formatPrice({
              amount: cartCost.setupFeeAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasBulkDiscount && (
        <PriceBreakdownRow type="bulk-discount">
          <PriceBreakdownLabel label={`${checkout.cartBulkDiscountText}`} type="bulk-discount" />
          <PriceBreakdownValue type="bulk-discount">
            -
            {formatPrice({
              amount: cartCost.totalBulkDiscountAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasCoupon && (
        <PriceBreakdownRow type="discount">
          <PriceBreakdownLabel
            label={
              cartCost.coupon.percentOff
                ? `${checkout.cartCouponDiscountText} (${cartCost.coupon.percentOff}%)`
                : checkout.cartCouponDiscountText
            }
            type="discount"
          />
          <PriceBreakdownValue type="discount">
            -
            {formatPrice({
              amount: cartCost.couponDiscountAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.fees &&
        cartCost.fees.map(fee => (
          <PriceBreakdownRow key={fee.name} type="fee">
            <PriceBreakdownLabel
              label={fee.percentage ? `${fee.name} (${fee.percentage}%)` : fee.name}
              type="fee"
            />
            <PriceBreakdownValue type="fee">
              {formatPrice({
                amount: fee.amount,
                currency: cartCost.currency,
                locale: checkout.locale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        ))}

      {cartCost.oneTimeTaxAmounts?.length ? (
        cartCost.oneTimeTaxAmounts?.map(taxAmount => (
          <PriceBreakdownRow key={taxAmount.displayName} type="tax">
            <PriceBreakdownLabel
              label={
                <>
                  {taxAmount.displayName || checkout.cartTaxText} ({taxAmount.percentage}%)
                  <InfoTooltip>Tax is determined by billing information</InfoTooltip>:{' '}
                </>
              }
              type="tax"
            />

            <PriceBreakdownValue type="tax">
              {formatPrice({
                amount: taxAmount.amount,
                currency: cartCost.currency,
                locale: checkout.locale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        ))
      ) : (
        <PriceBreakdownRow type="tax">
          <PriceBreakdownLabel label={checkout.cartTaxText} type="tax" />
          <PriceBreakdownValue type="tax">
            {formatPrice({
              amount: cartCost.oneTimeTaxAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {paymentOption.type === 'partial' && (
        <PriceBreakdownRow type="due-now">
          <PriceBreakdownLabel label={checkout.cartDueNowText} type="due-now" />
          <PriceBreakdownValue type="due-now">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {paymentOption.type !== 'partial' && (
        <PriceBreakdownRow type="total">
          <PriceBreakdownLabel label={checkout.cartTotalDueText} type="total" />
          <PriceBreakdownValue type="total">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}
    </PriceBreakdownContainer>
  );
}

function RegularPriceBreakdown() {
  const cartCost = useCartCost();
  const { checkout, paymentOption } = useCheckoutState();

  const hasCoupon = !!cartCost.coupon;
  const hasDiscount = cartCost.totalDiscountAmount > 0;
  const hasBulkDiscount = cartCost.totalBulkDiscountAmount > 0;

  if (
    cartCost.subtotalAmount === cartCost.totalDueAmount &&
    !cartCost.setupFeeAmount &&
    !hasDiscount &&
    !hasCoupon &&
    !cartCost.fees
  ) {
    return (
      <PriceBreakdownContainer>
        <PriceBreakdownRow type="total">
          <PriceBreakdownLabel label={checkout.cartTotalDueText} type="total" />
          <PriceBreakdownValue type="total">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      </PriceBreakdownContainer>
    );
  }

  return (
    <PriceBreakdownContainer>
      <PriceBreakdownRow type="subtotal">
        <PriceBreakdownLabel label={checkout.cartSubtotalText} type="subtotal" />
        <PriceBreakdownValue type="subtotal">
          {formatPrice({
            amount: cartCost.subtotalAmount,
            currency: cartCost.currency,
            locale: checkout.locale,
            style: 'currency',
          })}
        </PriceBreakdownValue>
      </PriceBreakdownRow>

      {cartCost.setupFeeAmount > 0 && (
        <PriceBreakdownRow type="setup-fee">
          <PriceBreakdownLabel label={checkout.cartSetupFeeText} type="setup-fee" />
          <PriceBreakdownValue type="setup-fee">
            {formatPrice({
              amount: cartCost.setupFeeAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasBulkDiscount && (
        <PriceBreakdownRow type="bulk-discount">
          <PriceBreakdownLabel label={`${checkout.cartBulkDiscountText}`} type="bulk-discount" />
          <PriceBreakdownValue type="bulk-discount">
            -
            {formatPrice({
              amount: cartCost.totalBulkDiscountAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {hasCoupon && (
        <PriceBreakdownRow type="discount">
          <PriceBreakdownLabel
            label={
              cartCost.coupon.percentOff
                ? `${checkout.cartCouponDiscountText} (${cartCost.coupon.percentOff}%)`
                : checkout.cartCouponDiscountText
            }
            type="discount"
          />
          <PriceBreakdownValue type="discount">
            -
            {formatPrice({
              amount: cartCost.couponDiscountAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {cartCost.fees &&
        cartCost.fees.map(fee => (
          <PriceBreakdownRow key={fee.name} type="fee">
            <PriceBreakdownLabel
              label={fee.percentage ? `${fee.name} (${fee.percentage}%)` : fee.name}
              type="fee"
            />
            <PriceBreakdownValue type="fee">
              {formatPrice({
                amount: fee.amount,
                currency: cartCost.currency,
                locale: checkout.locale,
                style: 'currency',
              })}
            </PriceBreakdownValue>
          </PriceBreakdownRow>
        ))}

      {paymentOption.type === 'partial' && (
        <PriceBreakdownRow type="due-now">
          <PriceBreakdownLabel label={checkout.cartDueNowText} type="due-now" />
          <PriceBreakdownValue type="due-now">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}

      {paymentOption.type !== 'partial' && (
        <PriceBreakdownRow type="total">
          <PriceBreakdownLabel label={checkout.cartTotalDueText} type="total" />
          <PriceBreakdownValue type="total">
            {formatPrice({
              amount: cartCost.totalDueAmount,
              currency: cartCost.currency,
              locale: checkout.locale,
              style: 'currency',
            })}
          </PriceBreakdownValue>
        </PriceBreakdownRow>
      )}
    </PriceBreakdownContainer>
  );
}
